import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface DatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (date: string) => void;
}

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function toYMD(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDisplay(ymd: string): string {
  const [y, m, d] = ymd.split('-');
  return `${MONTHS[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

function getCalendarDays(year: number, month: number): (number | null)[] {
  const firstDay = new Date(year, month, 1).getDay();
  // Convert Sunday=0 to Monday-based: Mon=0 … Sun=6
  const startOffset = (firstDay + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // Fill trailing cells to complete last row
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function shiftDate(ymd: string, days: number): string {
  const d = new Date(ymd + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return toYMD(d);
}

const btnReset: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const DatePicker: React.FC<DatePickerProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const parsed = new Date(value + 'T00:00:00');
  const [viewYear, setViewYear] = useState(parsed.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed.getMonth());
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync calendar view when value changes externally
  useEffect(() => {
    const d = new Date(value + 'T00:00:00');
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const prevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const nextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const selectDay = (day: number) => {
    const newDate = `${viewYear}-${pad(viewMonth + 1)}-${pad(day)}`;
    onChange(newDate);
    setOpen(false);
  };

  const goToday = () => {
    const today = toYMD(new Date());
    onChange(today);
    setOpen(false);
  };

  const cells = getCalendarDays(viewYear, viewMonth);
  const selectedDay =
    parsed.getFullYear() === viewYear && parsed.getMonth() === viewMonth ? parsed.getDate() : null;
  const today = new Date();
  const todayDay =
    today.getFullYear() === viewYear && today.getMonth() === viewMonth ? today.getDate() : null;

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
    >
      {/* Single unified control */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        <button
          onClick={() => onChange(shiftDate(value, -1))}
          style={{
            ...btnReset,
            width: 32,
            height: 32,
            color: 'var(--text-secondary)',
            borderRight: '1px solid var(--border)',
          }}
          title="Previous day"
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--sidebar-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <ChevronLeftIcon style={{ fontSize: 16 }} />
        </button>

        <button
          onClick={() => setOpen((o) => !o)}
          style={{
            ...btnReset,
            padding: '6px 14px',
            color: 'var(--text-primary)',
            fontSize: 13,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--sidebar-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          {formatDisplay(value)}
        </button>

        <button
          onClick={() => onChange(shiftDate(value, 1))}
          style={{
            ...btnReset,
            width: 32,
            height: 32,
            color: 'var(--text-secondary)',
            borderLeft: '1px solid var(--border)',
          }}
          title="Next day"
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--sidebar-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <ChevronRightIcon style={{ fontSize: 16 }} />
        </button>
      </div>

      {/* Calendar popup */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 1000,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 12,
            boxShadow: 'var(--shadow-elevated)',
            width: 264,
          }}
        >
          {/* Month/Year header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <button
              onClick={prevMonth}
              style={{
                ...btnReset,
                color: 'var(--text-secondary)',
                width: 28,
                height: 28,
                borderRadius: 4,
              }}
            >
              <ChevronLeftIcon style={{ fontSize: 20 }} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button
              onClick={nextMonth}
              style={{
                ...btnReset,
                color: 'var(--text-secondary)',
                width: 28,
                height: 28,
                borderRadius: 4,
              }}
            >
              <ChevronRightIcon style={{ fontSize: 20 }} />
            </button>
          </div>

          {/* Day headers */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0,
              marginBottom: 4,
            }}
          >
            {DAYS.map((d) => (
              <div
                key={d}
                style={{
                  textAlign: 'center',
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--text-tertiary)',
                  padding: '4px 0',
                  textTransform: 'uppercase',
                }}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((day, i) => {
              if (day === null) return <div key={`e-${i}`} />;
              const isSelected = day === selectedDay;
              const isToday = day === todayDay;
              return (
                <button
                  key={day}
                  onClick={() => selectDay(day)}
                  style={{
                    ...btnReset,
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    margin: '0 auto',
                    fontSize: 13,
                    fontFamily: 'var(--font-mono)',
                    fontWeight: isSelected || isToday ? 600 : 400,
                    color: isSelected ? '#fff' : isToday ? 'var(--accent)' : 'var(--text-primary)',
                    background: isSelected ? 'var(--accent)' : 'transparent',
                    border:
                      isToday && !isSelected
                        ? '1px solid var(--accent-border)'
                        : '1px solid transparent',
                    transition: 'background 100ms ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLElement).style.background = 'var(--sidebar-hover)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                  }}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 8,
              paddingTop: 8,
              borderTop: '1px solid var(--border)',
            }}
          >
            <button
              onClick={goToday}
              style={{
                ...btnReset,
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--accent)',
                padding: '4px 8px',
                borderRadius: 4,
              }}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
